import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaFilePdf } from 'react-icons/fa';
import Loading from '../components/common/Loading';
import { getColors, getProduct } from '../lib/api';

const ProductDetail = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [selectedColor, setSelectedColor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [colors, setColors] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [isFaqImageOpen, setIsFaqImageOpen] = useState(false);



  const fetchColors = useCallback(async () => {
    try {
      const data = await getColors({ visible: true });
      setColors(data || []);
    } catch (error) {
      console.error('Error fetching colors:', error);
    }
  }, []);

  const fetchProduct = useCallback(async () => {
    try {
      setLoading(true);
      const productData = await getProduct(slug);
      setProduct(productData);
      setGalleryImages(productData.product_images || []);
    } catch (error) {
      console.error('Error fetching product:', error);
      setProduct(null);
      setGalleryImages([]);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchColors();
    fetchProduct();
  }, [fetchColors, fetchProduct]);

  // Renk değiştiğinde ilk görseli seç
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [selectedColor, galleryImages]);

  // Filtrelenmiş galeri görselleri
  const filteredGalleryImages = useMemo(() => {
    return selectedColor
      ? galleryImages.filter(img => img.color_id === selectedColor)
      : galleryImages;
  }, [selectedColor, galleryImages]);

  // Klavye ok tuşları ile görsel değiştirme
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (filteredGalleryImages.length === 0) return;

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setCurrentImageIndex((prev) =>
          prev === 0 ? filteredGalleryImages.length - 1 : prev - 1
        );
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setCurrentImageIndex((prev) =>
          prev === filteredGalleryImages.length - 1 ? 0 : prev + 1
        );
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [filteredGalleryImages]);

  // ESC tuşu ile FAQ görsel modalını kapatma
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && isFaqImageOpen) {
        setIsFaqImageOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, [isFaqImageOpen]);

  if (loading) {
    return <Loading message="Ürün detayları yükleniyor..." />;
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Ürün bulunamadı</h2>
          <p className="text-gray-600 mb-4">Aradığınız ürün mevcut değil veya kaldırılmış olabilir.</p>
          <Link to="/products" className="text-[#dbc582] hover:text-[#c8bca4] transition-colors">
            Ürün listesine dön
          </Link>
        </motion.div>
      </div>
    );
  }



  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="container mx-auto px-4 py-8"
    >
      <h1 className="text-3xl font-bold mb-6">{product.title}</h1>

      <div className="bg-white rounded-lg p-6 shadow-lg mb-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Sol Sütun - Görseller */}
          <div className="flex flex-col h-full">
            {/* Ana Görsel */}
            <div className="mb-4 flex-grow">
              {filteredGalleryImages.length > 0 ? (
                <img
                  src={filteredGalleryImages[currentImageIndex].image_url}
                  alt={product.title}
                  className="w-full h-[400px] md:h-[500px] lg:h-[600px] object-contain bg-white rounded-lg"
                />
              ) : (
                <div className="w-full h-[400px] md:h-[500px] lg:h-[600px] bg-gray-100 rounded-lg flex flex-col items-center justify-center">
                  <svg
                    className="w-24 h-24 text-gray-300 mb-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <p className="text-gray-500 text-lg">Bu renk için görsel bulunamadı</p>
                </div>
              )}
            </div>

            {/* Küçük Resimler */}
            <div className="flex gap-2 overflow-x-auto pb-2">
              {filteredGalleryImages.map((image, index) => (
                <button
                  key={image.id}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 ${index === currentImageIndex ? 'border-[#dbc582]' : 'border-transparent'
                    }`}
                >
                  <img
                    src={image.image_url}
                    alt={`${product.title} ${image.colors?.name || ''}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Sağ Sütun - Ürün Bilgileri */}
          <div className="h-full flex flex-col">
            <h2 className="text-2xl font-semibold mb-4">
              {selectedColor
                ? colors.find(c => c.id === selectedColor)?.name
                : product.title}
            </h2>
            <p className="text-gray-600 mb-6 break-words overflow-auto max-h-32 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 pr-2">
              {product.description}
            </p>

            {/* Renk Seçenekleri */}
            <div className="mb-8">
              <h3 className="text-lg font-medium mb-3">Renk Seçenekleri</h3>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setSelectedColor(null)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full ${!selectedColor ? 'bg-gray-100 ring-2 ring-[#dbc582]' : 'hover:bg-gray-50'
                    }`}
                >
                  <span className="w-4 h-4 rounded-full bg-gradient-to-r from-gray-300 to-gray-400" />
                  <span>Tümü</span>
                </button>
                {colors.filter(color =>
                  galleryImages.some(img => img.color_id === color.id)
                ).map(color => (
                  <button
                    key={color.id}
                    onClick={() => setSelectedColor(color.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full ${selectedColor === color.id ? 'bg-gray-100 ring-2 ring-[#dbc582]' : 'hover:bg-gray-50'
                      }`}
                  >
                    <span
                      className="w-4 h-4 rounded-full border border-gray-200"
                      style={{ backgroundColor: color.hex_code }}
                    />
                    <span>{color.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Özellikler */}
            <div className="mb-8">
              <h3 className="text-lg font-medium mb-3">Özellikler</h3>
              <div className="grid grid-cols-2 gap-4">
                {Array.isArray(product.features) && product.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <svg
                      className="w-5 h-5 text-[#dbc582] flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span>{typeof feature === 'string' ? feature : feature?.title || ''}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Teknik Özellikler */}
            <div>
              <h3 className="text-lg font-medium mb-3">Teknik Özellikler</h3>
              <div className="grid gap-2">
                {Array.isArray(product.technical_specs) && product.technical_specs.map((spec, index) => (
                  <div key={index} className="flex justify-between py-2 border-b border-gray-200 last:border-b-0">
                    <span className="font-medium">{spec?.title || ''}</span>
                    <span className="text-gray-600">{spec?.value || ''}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Bölümü */}
      {Array.isArray(product.faq) && product.faq.length > 0 && (
        <div className="bg-white rounded-lg p-6 shadow-lg mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Sol Sütun - FAQ Listesi */}
            <div>
              <h2 className="text-2xl font-bold mb-6">Sıkça Sorulan Sorular</h2>
              <div className="space-y-2">
                {product.faq.map((item, index) => (
                  <div key={index} className="border-b border-gray-200 last:border-b-0">
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                      className="w-full py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors rounded-lg px-4"
                    >
                      <span className="font-medium text-gray-900">{item?.question || ''}</span>
                      <svg
                        className={`w-5 h-5 text-gray-500 transform transition-transform ${openFaqIndex === index ? 'rotate-180' : ''
                          }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {openFaqIndex === index && (
                      <div className="px-4 pb-4">
                        <p className="text-gray-600">{item?.answer || ''}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Sağ Sütun - FAQ Görseli */}
            <div className="flex items-center justify-center">
              {product.faq_image ? (
                <div
                  className="relative group cursor-pointer"
                  onClick={() => {
                    console.log('FAQ image clicked!');
                    setIsFaqImageOpen(true);
                  }}
                >
                  <img
                    src={product.faq_image}
                    alt="FAQ İllüstrasyon"
                    className="w-full min-h-[300px] max-h-[400px] object-contain rounded-lg shadow-lg transition-transform duration-300 group-hover:scale-105"
                  />
                  {/* Zoom Icon Overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 rounded-lg flex items-center justify-center pointer-events-none">
                    <div className="bg-white/90 p-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-lg">
                      <svg
                        className="w-6 h-6 text-gray-800"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="w-full min-h-[300px] max-h-[400px] bg-gray-100 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-24 h-24 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9.86 0 01-18 0 9.86 0 0118 0z"
                    />
                  </svg>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* İletişim Bölümü */}
      <div className="bg-white rounded-lg p-6 shadow-lg">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Sol Sütun - Ürün Bilgisi */}
          <div className="flex items-center space-x-6">
            <div className="flex-shrink-0">
              <img
                src={filteredGalleryImages[currentImageIndex]?.image_url || product.main_image}
                alt={product.title}
                className="w-32 h-32 object-cover rounded-lg shadow-md"
              />
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">{product.title}</h3>
              <p className="text-gray-600 mb-4">
                {selectedColor
                  ? `Seçili Renk: ${colors.find(c => c.id === selectedColor)?.name}`
                  : 'Tüm Renkler'}
              </p>
            </div>
          </div>

          {/* Sağ Sütun - İletişim Butonları */}
          <div className="flex flex-col lg:flex-row items-center justify-end gap-4">
            <a
              href={`https://wa.me/+905387416525?text=Merhaba, ${product.title} ürünü hakkında bilgi almak istiyorum.${selectedColor ? `%0A%0ASeçili Renk: ${colors.find(c => c.id === selectedColor)?.name}` : ''}%0A%0AÜrün linki: ${window.location.href}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full lg:w-auto px-6 py-3 bg-green-600 text-white rounded-lg flex items-center justify-center hover:bg-green-700 transition-colors"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              WhatsApp ile İletişim
            </a>
            <a
              href="tel:+905387416525"
              className="w-full lg:w-auto px-6 py-3 bg-[#dbc582] text-white rounded-lg flex items-center justify-center hover:bg-[#c5b172] transition-colors"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              Hemen Ara
            </a>
            {['facade', 'baffle_ceiling', 'flat_panel'].includes(product.category) && (
              <>
                <a
                  href="/docs/kartela-katalog.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full lg:w-auto px-6 py-3 border-2 border-[#dbc582] text-[#dbc582] rounded-lg flex items-center justify-center hover:bg-[#dbc582]/10 transition-colors"
                >
                  <FaFilePdf className="w-5 h-5 mr-2" />
                  Kartela
                </a>
                <a
                  href="/docs/cephe-brosur.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full lg:w-auto px-6 py-3 border-2 border-[#dbc582] text-[#dbc582] rounded-lg flex items-center justify-center hover:bg-[#dbc582]/10 transition-colors"
                >
                  <FaFilePdf className="w-5 h-5 mr-2" />
                  Broşür
                </a>
              </>
            )}
            {product.category === 'deck' && (
              <a
                href="/docs/deck-katalog.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full lg:w-auto px-6 py-3 border-2 border-[#dbc582] text-[#dbc582] rounded-lg flex items-center justify-center hover:bg-[#dbc582]/10 transition-colors"
              >
                <FaFilePdf className="w-5 h-5 mr-2" />
                Katalog
              </a>
            )}
          </div>
        </div>
      </div>

      {/* FAQ Image Lightbox Modal */}
      {isFaqImageOpen && product.faq_image && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setIsFaqImageOpen(false)}
        >
          <div className="relative max-w-[95vw] max-h-[95vh] w-full h-full flex items-center justify-center">
            {/* Close Button */}
            <button
              onClick={() => setIsFaqImageOpen(false)}
              className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white p-2 rounded-full transition-colors z-10"
              aria-label="Kapat"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Enlarged Image */}
            <img
              src={product.faq_image}
              alt="FAQ İllüstrasyon - Büyütülmüş"
              className="object-contain rounded-lg cursor-zoom-in"
              onClick={(e) => e.stopPropagation()}
              style={{ width: 'auto', height: 'auto', maxWidth: '95vw', maxHeight: '95vh', minWidth: '60vw', minHeight: '60vh' }}
            />
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default ProductDetail;
